"use client";

import ReactQueryClientProvider from "@app/_components/ReactQueryClientProvider/ReactQueryClientProvider";
import { SiteInfoProvider } from "@context/siteInfoContext";
import type { RootLayoutQuery } from "@server/gql/graphql";
import { LazyMotion } from "framer-motion";
import type { ReactNode } from "react";

import { enableClientDiagnostics } from "@/utils/diagnostics/enableClientDiagnostics";
import { AuthProvider } from "@features/auth/components/AuthProvider";
import MarketSelectorDialog from "@features/market/components/MarketSelectorDialog";
import { Market } from "@features/market/config/i18n-config";
import dynamic from "next/dynamic";

const loadMotionFeatures = () =>
  import("framer-motion").then((mod) => mod.domAnimation);

const VoyadoOnsite = dynamic(
  () => import("@app/_components/VoyadoOnsite/VoyadoOnsite"),
  { ssr: false },
);

const ReactQueryDevtools =
  process.env.NODE_ENV === "development"
    ? dynamic(
        () =>
          import("@tanstack/react-query-devtools").then(
            (mod) => mod.ReactQueryDevtools,
          ),
        { ssr: false },
      )
    : () => null;

type ProviderProps = {
  children: ReactNode;
  siteinfoCollection: RootLayoutQuery["siteinfoCollection"] | null;
  campaigns: RootLayoutQuery["campaigns"] | null;
  wishlist: RootLayoutQuery["wishlist"] | null;
  market: Market;
};

export default function Provider({
  children,
  siteinfoCollection,
  campaigns,
  wishlist,
  market,
}: ProviderProps) {
  enableClientDiagnostics();

  return (
    <ReactQueryClientProvider>
      <LazyMotion features={loadMotionFeatures} strict>
        <SiteInfoProvider
          siteinfoCollection={siteinfoCollection ?? undefined}
          campaigns={campaigns ?? undefined}
          wishlist={wishlist ?? undefined}
          market={market}
        >
          <AuthProvider market={market}>
            <MarketSelectorDialog />
            <VoyadoOnsite />
            {children}
          </AuthProvider>
        </SiteInfoProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </LazyMotion>
    </ReactQueryClientProvider>
  );
}
